import { create } from 'zustand';
import { toast } from 'sonner';
import type { ScoredTrend } from '@/hooks/use-trends';

const MAX_BACKOFF = 30_000;
const INITIAL_BACKOFF = 1_000;

interface WebSocketStore {
  isConnected: boolean;
  newTrends: ScoredTrend[];
  newForecastCount: number;
  connect: () => void;
  disconnect: () => void;
  clearNewTrends: () => void;
  clearNewForecasts: () => void;
}

let ws: WebSocket | null = null;
let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
let reconnectAttempts = 0;

function getBackoffDelay(): number {
  const base = Math.min(INITIAL_BACKOFF * Math.pow(2, reconnectAttempts), MAX_BACKOFF);
  const jitter = Math.random() * 1000;
  return base + jitter;
}

export const useWebSocketStore = create<WebSocketStore>((set, get) => ({
  isConnected: false,
  newTrends: [],
  newForecastCount: 0,

  connect: () => {
    // Don't connect if already connected or on server
    if (typeof window === 'undefined') return;
    if (ws && ws.readyState === WebSocket.OPEN) return;

    // Clean up existing connection
    if (ws) {
      ws.onclose = null;
      ws.onerror = null;
      ws.onmessage = null;
      ws.close();
    }

    // Determine WebSocket URL - same origin, cookies sent automatically
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/api/v1/ws/trends`;

    try {
      ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        reconnectAttempts = 0;
        set({ isConnected: true });
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'forecast' || msg.type === 'forecast_updated') {
            // Forecast notification
            set((state) => ({
              newForecastCount: state.newForecastCount + 1,
            }));
            // Show toast notification with action tag
            const recStatus = msg.recommendation_status as string | undefined;
            const tagMap: Record<string, string> = {
              launch_now: 'Запускать сейчас',
              wait_confirmation: 'Ждём подтверждения',
              needs_test: 'Нужен тест',
              needs_budget: 'Нужен бюджет',
            };
            const tag = recStatus ? tagMap[recStatus] : undefined;
            const desc = [msg.title, tag].filter(Boolean).join(' — ');
            toast(msg.type === 'forecast_updated' ? 'Прогноз обновлён' : 'Новый прогноз', {
              description: desc || undefined,
            });
          } else {
            // Legacy trend handling (no type field on trend messages)
            const trend = msg as ScoredTrend;
            set((state) => ({
              newTrends: [trend, ...state.newTrends],
            }));
          }
        } catch {
          // Ignore malformed messages
        }
      };

      ws.onclose = () => {
        set({ isConnected: false });
        ws = null;

        // Only reconnect if page is visible
        if (document.visibilityState === 'visible') {
          const delay = getBackoffDelay();
          reconnectAttempts++;
          reconnectTimeout = setTimeout(() => {
            get().connect();
          }, delay);
        }
      };

      ws.onerror = () => {
        // onclose will be called after onerror
      };
    } catch {
      // Connection failed, will retry via onclose
    }
  },

  disconnect: () => {
    if (reconnectTimeout) {
      clearTimeout(reconnectTimeout);
      reconnectTimeout = null;
    }
    reconnectAttempts = 0;

    if (ws) {
      ws.onclose = null; // Prevent reconnection on intentional disconnect
      ws.close();
      ws = null;
    }

    set({ isConnected: false });
  },

  clearNewTrends: () => {
    set({ newTrends: [] });
  },

  clearNewForecasts: () => {
    set({ newForecastCount: 0 });
  },
}));

// Reconnect when page becomes visible
if (typeof window !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      const store = useWebSocketStore.getState();
      if (!store.isConnected) {
        store.connect();
      }
    }
  });
}
